import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { StandaloneNotice } from '@/features/pwa/StandaloneNotice';
import { UpdatePrompt } from '@/features/pwa/UpdatePrompt';
import { FormularioPerfil } from '@/features/more/FormularioPerfil';
import { Encabezado } from '@/app/Encabezado';
import { actualizarSettings, leerSettings } from '@/db/schema';
import { mensajeDeError } from '@/db/tx';
import { useFechaHoy } from '@/lib/useFechaHoy';

/**
 * Primeros pasos (Req. 4.1, 4.2): aparece la primera vez que se abre la app.
 *
 * Pregunta los datos que alguna pantalla lee o algún cálculo usa: el nombre,
 * las unidades, la estatura y los tres de la fórmula del mantenimiento
 * (nacimiento, sexo y actividad), cada uno con su para qué (0.11.0). Todo es
 * opcional y se puede saltar; se edita después en Más → Perfil.
 */
export function OnboardingPage() {
  const hoy = useFechaHoy();
  const settings = useLiveQuery(() => leerSettings(), []);
  const navegar = useNavigate();
  const [saltando, setSaltando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (settings === undefined) return null;
  if (settings.primerosPasosHechos) return <Navigate to="/" replace />;

  async function saltar() {
    setSaltando(true);
    setError(null);
    try {
      await actualizarSettings({ primerosPasosHechos: true });
      void navegar('/', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
      setSaltando(false);
    }
  }

  return (
    <div className="min-h-dvh bg-fondo pb-segura">
      <div className="mx-auto w-full max-w-lg pt-segura">
        <Encabezado conPerfil={false} />
      </div>

      <main className="mx-auto w-full max-w-lg space-y-5 px-[18px] pb-8 pt-[11px] min-[401px]:px-[23px]">
        <div className="space-y-3 empty:hidden">
          <UpdatePrompt />
          <StandaloneNotice />
        </div>

        <PageHeader
          antetitulo="Primeros pasos"
          titulo="Te damos la bienvenida a DuoFit"
          descripcion="Solo lo que la app usa: cada dato dice para qué se pide. Todo es opcional, lo puedes saltar y cambiarlo después en Más → Perfil."
        />

        <Banner tono="info" titulo="Tus datos se quedan en este teléfono">
          No hay cuentas ni servidor. Cada teléfono tiene su propio perfil, y los respaldos se hacen
          desde Más → Respaldos.
        </Banner>

        {error !== null && (
          <Banner tono="alerta" titulo="No se pudo continuar">
            {error}
          </Banner>
        )}

        <FormularioPerfil
          settings={settings}
          hoy={hoy}
          textoGuardar="Guardar y empezar"
          onGuardar={async (cambios) => {
            await actualizarSettings({ ...cambios, primerosPasosHechos: true });
            void navegar('/', { replace: true });
          }}
          secundaria={
            <Button variante="fantasma" disabled={saltando} onClick={() => void saltar()}>
              {saltando ? 'Un momento…' : 'Saltar por ahora'}
            </Button>
          }
        />
      </main>
    </div>
  );
}
